import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

function makeCode(): string {
  let out = "";
  const bytes = new Uint8Array(7);
  crypto.getRandomValues(bytes);
  for (const b of bytes) out += ALPHABET[b % ALPHABET.length];
  return out;
}

function normalizeCode(value: unknown): string {
  return String(value ?? "").trim().toUpperCase().slice(0, 16);
}

export const getReferralStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const email = String((context.claims as Record<string, unknown>)?.["email"] ?? "");

    const { data: profile } = await context.supabase
      .from("profiles")
      .select("referral_code, free_months, free_months_used, billing_period_end, trial_ends_at, referred_by, beta_status")
      .eq("user_id", context.userId)
      .maybeSingle();

    let code = profile?.referral_code ?? null;

    if (!code) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      for (let i = 0; i < 5 && !code; i++) {
        const candidate = makeCode();
        const { error } = await supabaseAdmin
          .from("profiles")
          .upsert(
            { user_id: context.userId, email, referral_code: candidate },
            { onConflict: "user_id" },
          );
        if (!error) code = candidate;
      }
    }

    const [{ data: referrals }, { data: credits }] = await Promise.all([
      context.supabase
        .from("referrals")
        .select("created_at")
        .eq("referrer_user_id", context.userId)
        .order("created_at", { ascending: false }),
      context.supabase
        .from("billing_credits")
        .select("id, months, status, granted_at, applied_at, expires_at, note")
        .eq("user_id", context.userId)
        .order("granted_at", { ascending: false }),
    ]);

    const joined = referrals?.length ?? 0;
    const available = (credits ?? []).filter((c) => c.status === "available");
    const applied = (credits ?? []).filter((c) => c.status === "applied");

    // Free beta seats don't earn referral credit — there's nothing to discount
    // yet, and it would be trivially farmable while signups are free.
    const betaActive = profile?.beta_status === "active";

    return {
      eligible: !betaActive,
      code,
      joined,
      freeMonths: available.reduce((n, c) => n + (c.months ?? 0), 0),
      freeMonthsUsed: applied.reduce((n, c) => n + (c.months ?? 0), 0),
      billingPeriodEnd: profile?.billing_period_end ?? null,
      credits: (credits ?? []).slice(0, 24),
      towardNext: joined % 3,
      trialEndsAt: profile?.trial_ends_at ?? null,
      wasReferred: Boolean(profile?.referred_by),
      recent: (referrals ?? []).slice(0, 10).map((r) => r.created_at),
    };
  });

/**
 * Stores an invite code server-side against the email the visitor is about to
 * sign up with, so the referral survives even if they finish signing up (or
 * confirm their email) on a completely different device or browser.
 */
export const recordPendingReferral = createServerFn({ method: "POST" })
  .inputValidator((input: { email: string; code: string }) => ({
    email: String(input?.email ?? "").trim().toLowerCase().slice(0, 320),
    code: normalizeCode(input?.code),
  }))
  .handler(async ({ data }) => {
    if (!data.email || !data.email.includes("@") || !data.code) return { ok: false as const };

    const { callerKey, rateLimit } = await import("./rate-limit.server");
    const verdict = await rateLimit("referral_pending", callerKey("referral"), 10, 600);
    if (!verdict.allowed) return { ok: false as const };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Only remember codes that actually belong to someone.
    const { data: referrer } = await supabaseAdmin
      .from("profiles")
      .select("user_id")
      .eq("referral_code", data.code)
      .maybeSingle();
    if (!referrer) return { ok: false as const };

    // First invite wins: an anonymous caller can never replace or erase an
    // invite someone else already saved for this email. Only expired rows go.
    const nowIso = new Date().toISOString();
    const { data: active } = await supabaseAdmin
      .from("referral_pending")
      .select("code")
      .ilike("email", data.email)
      .gt("expires_at", nowIso)
      .maybeSingle();
    if (active) return { ok: active.code === data.code };

    await supabaseAdmin
      .from("referral_pending")
      .delete()
      .ilike("email", data.email)
      .lte("expires_at", nowIso);
    const { error } = await supabaseAdmin.from("referral_pending").insert({
      email: data.email,
      code: data.code,
      expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    });

    return { ok: !error };
  });

export const claimReferral = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input?: { code?: string }) => ({ code: normalizeCode(input?.code) }))
  .handler(async ({ data, context }) => {
    const email = String((context.claims as Record<string, unknown>)?.["email"] ?? "");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: mine } = await supabaseAdmin
      .from("profiles")
      .select("referred_by, referral_code")
      .eq("user_id", context.userId)
      .maybeSingle();

    if (mine?.referred_by) return { ok: false as const, reason: "You've already used an invite." };

    // Device-provided code first, then the server-side record tied to this email.
    let code = data.code;
    if (!code && email) {
      const { data: pending } = await supabaseAdmin
        .from("referral_pending")
        .select("code, expires_at")
        .ilike("email", email)
        .maybeSingle();
      if (pending && new Date(pending.expires_at).getTime() > Date.now()) code = pending.code;
    }

    if (!code) return { ok: false as const, reason: "No invite code." };
    if (mine?.referral_code === code) {
      return { ok: false as const, reason: "That's your own invite code." };
    }

    const { data: referrer } = await supabaseAdmin
      .from("profiles")
      .select("user_id")
      .eq("referral_code", code)
      .maybeSingle();

    if (!referrer) return { ok: false as const, reason: "That invite code isn't valid." };

    const trialEnds = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();

    const { data: referralRow, error: refErr } = await supabaseAdmin
      .from("referrals")
      .insert({
        referrer_user_id: referrer.user_id,
        referred_user_id: context.userId,
        code,
      })
      .select("id")
      .maybeSingle();
    if (refErr) return { ok: false as const, reason: "You've already used an invite." };

    await supabaseAdmin
      .from("profiles")
      .upsert(
        { user_id: context.userId, email, referred_by: referrer.user_id, trial_ends_at: trialEnds },
        { onConflict: "user_id" },
      );

    if (email) await supabaseAdmin.from("referral_pending").delete().ilike("email", email);

    const { count } = await supabaseAdmin
      .from("referrals")
      .select("id", { count: "exact", head: true })
      .eq("referrer_user_id", referrer.user_id);

    const { data: referrerProfile } = await supabaseAdmin
      .from("profiles")
      .select("beta_status")
      .eq("user_id", referrer.user_id)
      .maybeSingle();

    const joined = count ?? 0;
    if (
      referrerProfile?.beta_status !== "active" &&
      joined > 0 &&
      joined % 3 === 0 &&
      referralRow?.id
    ) {
      // Real, consumable credit: one free month recorded in the billing ledger
      // and the referrer's next billing date pushed back 30 days.
      await supabaseAdmin.rpc("grant_referral_credit", {
        _user_id: referrer.user_id,
        _referral_id: referralRow.id,
      });
    }

    return { ok: true as const, trialEndsAt: trialEnds };
  });

/**
 * Billing hook. A future billing integration calls this right before charging:
 * if a free-month credit is available it is consumed and the caller skips the
 * charge for that period. Returns { applied: false } when there's nothing to use.
 */
export const consumeFreeMonth = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input?: { reference?: string }) => ({
    reference: String(input?.reference ?? "").slice(0, 120),
  }))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: result, error } = await supabaseAdmin.rpc("consume_billing_credit", {
      _user_id: context.userId,
      ...(data.reference ? { _reference: data.reference } : {}),
    });
    if (error) throw new Error(error.message);
    return (result ?? { applied: false, months: 0 }) as { applied: boolean; months: number };
  });
